import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { useAuth } from '../context/AuthContext';
import {
  alreadyConfirmed, authenticate, clearSignupCredentials, matchesSignupAccount,
  signupCredentials, signupDestination, temporarySignInError,
  signupSetupFailed, signupSetupErrorMessage, verificationErrorMessage,
} from '../utils/signupFlow';

const pool = new CognitoUserPool({ UserPoolId: 'eu-west-2_nyRkWiHVC', ClientId: 'fsp09nn4h6a084bod215if65q' });

export default function useSignupVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { email, signupId, signupSub } = location.state || {};
  const attributeMode = useRef(Boolean(location.state?.attributeVerification));
  const returnTo = location.state?.returnTo;
  const hasEmail = typeof email === 'string' && Boolean(email.trim());
  const identity = { email, signupSub };
  const credentials = useRef(signupCredentials(signupId, email));
  const verified = useRef(Boolean(location.state?.verified));
  const setupFailed = useRef(Boolean(location.state?.setupFailed));
  const [phase, setPhase] = useState(setupFailed.current ? 'setupFailed' : verified.current ? 'verified' : 'unverified');
  const [error, setError] = useState(setupFailed.current ? signupSetupErrorMessage : '');
  const [info, setInfo] = useState('');
  const operation = useRef({ id: 0 });
  const busy = useRef(false);
  const mounted = useRef(false);
  const retryTimer = useRef();
  const started = useRef(false);
  const actions = useRef();

  useEffect(() => {
    mounted.current = true;
    const request = operation.current;
    // Ownership transfers to the mounted page; abandoned signup records do not
    // retain the password in the module after visiting verification.
    clearSignupCredentials(signupId);
    return () => {
      mounted.current = false;
      request.id++;
      clearTimeout(retryTimer.current);
    };
  }, [signupId]);

  const current = id => mounted.current && id === operation.current.id;
  const begin = nextPhase => {
    if (busy.current) return null;
    busy.current = true;
    const id = ++operation.current.id;
    setError(''); setInfo(''); setPhase(nextPhase);
    return id;
  };
  const fail = (id, nextPhase, message) => {
    if (!current(id)) return;
    busy.current = false;
    setPhase(nextPhase); setError(message); setInfo('');
  };
  const complete = (id, attrs) => {
    if (!current(id)) return;
    if (!matchesSignupAccount(attrs, identity)) {
      credentials.current = null;
      fail(id, 'differentAccount', 'A different account is signed in. Log in with your signup email to continue.');
      return;
    }
    credentials.current = null;
    authRef.current.setIsAuthenticated(true);
    const destination = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.includes('\\') && !['/login', '/verify-email'].includes(returnTo.split(/[?#]/)[0]) ? returnTo : signupDestination(attrs);
    navigate(destination, { replace: true });
  };
  const loadAccount = async (id, useExistingAttributes = false, refreshSession = false) => {
    if (!current(id)) return;
    setPhase('loadingAccount');
    try {
      const existing = authRef.current.userAttributes;
      const attrs = useExistingAttributes && authRef.current.accountReady !== false && existing?.sub ? existing : await authRef.current.fetchUserAttributes({ refreshSession });
      if (!attrs?.sub) throw new Error('Account attributes are not available yet.');
      complete(id, attrs);
    } catch (err) {
      if (err.code === 'email_verification_required' && current(id)) {
        attributeMode.current = true; verified.current = false; busy.current = false;
        setPhase('emailUnverified'); setError('Please verify your email to finish setting up your account.');
        navigate('/verify-email', { replace: true, state: { email, signupId, signupSub: err.sub || signupSub, attributeVerification: true, returnTo } });
      } else fail(id, 'accountFailed', err.message || 'We could not load your account. Please try again.');
    }
  };
  const signIn = async (id, retries = 1) => {
    if (!current(id)) return;
    if (!credentials.current?.password) {
      busy.current = false;
      setPhase(verified.current ? 'verified' : 'setupFailed');
      return;
    }
    setPhase('signingIn');
    try {
      const user = new CognitoUser({ Username: email, Pool: pool });
      const result = await authenticate(user, email, credentials.current.password);
      if (!current(id)) return;
      credentials.current = null;
      setInfo('');
      localStorage.setItem('authToken', result.getAccessToken().getJwtToken());
      await loadAccount(id);
    } catch (err) {
      if (!current(id)) return;
      if (err.code === 'UserNotConfirmedException') {
        verified.current = false; setupFailed.current = false;
        fail(id, 'unverified', 'Your email still needs verification. Enter the code from your email.');
        navigate('/verify-email', { replace: true, state: { email, signupId, signupSub } });
      } else if (retries && temporarySignInError(err)) {
        setInfo('The connection was interrupted. Retrying sign-in once…');
        retryTimer.current = setTimeout(() => signIn(id, 0), 600);
      } else {
        fail(id, 'signInFailed', verified.current ? 'Your email is verified, but automatic sign-in did not finish. Retry sign-in or continue to the login page.' : 'Sign-in did not finish. Log in to retry account setup.');
      }
    }
  };
  const markVerified = () => {
    verified.current = true;
    // Replacing this entry also removes any password left by an older frontend.
    navigate('/verify-email', { replace: true, state: { email, signupId, signupSub, verified: true, attributeVerification: attributeMode.current, returnTo } });
  };
  const failSetup = (id, err) => {
    console.error('Signup account setup failed:', { code: err.code || err.name, message: err.message });
    setupFailed.current = true;
    fail(id, 'setupFailed', signupSetupErrorMessage);
    // Cognito confirmation and application-profile creation can have different
    // outcomes. Do not treat this trigger error as proof either completed.
    navigate('/verify-email', { replace: true, state: { email, signupId, signupSub, setupFailed: true } });
  };
  const verify = code => {
    if (!code.trim()) { setError('Please enter your verification code.'); return; }
    const id = begin('verifying');
    if (id === null) return;
    if (attributeMode.current) {
      authRef.current.verifyEmailAttribute(code.trim()).then(() => {
        if (!current(id)) return;
        markVerified(); loadAccount(id, false, true);
      }).catch(err => fail(id, 'emailUnverified', verificationErrorMessage(err)));
      return;
    }
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.confirmRegistration(code.trim(), true, err => {
      if (!current(id)) return;
      if (err && !alreadyConfirmed(err)) {
        if (signupSetupFailed(err)) { failSetup(id, err); return; }
        fail(id, 'unverified', verificationErrorMessage(err));
        return;
      }
      markVerified();
      signIn(id);
    });
  };
  const resend = () => {
    const id = begin('resending');
    if (id === null) return;
    if (attributeMode.current) {
      authRef.current.requestEmailVerification().then(() => {
        if (!current(id)) return;
        busy.current = false; setPhase('emailUnverified'); setInfo('A new verification code has been sent to your email.');
      }).catch(err => fail(id, 'emailUnverified', verificationErrorMessage(err, true)));
      return;
    }
    const user = new CognitoUser({ Username: email, Pool: pool });
    user.resendConfirmationCode(err => {
      if (!current(id)) return;
      if (alreadyConfirmed(err)) { markVerified(); signIn(id); return; }
      if (err) {
        if (signupSetupFailed(err)) { failSetup(id, err); return; }
        fail(id, 'unverified', verificationErrorMessage(err, true)); return;
      }
      busy.current = false;
      setPhase('unverified'); setInfo('A new verification code has been sent to your email.');
    });
  };
  const continueToLogin = () => {
    credentials.current = null;
    operation.current.id++;
    clearTimeout(retryTimer.current);
    if (attributeMode.current) navigate(`/login?next=${encodeURIComponent(returnTo || '/home')}`, { replace: true, state: { email } });
    else navigate('/login', { replace: true, state: { signup: identity } });
  };
  actions.current = {
    start: () => {
      if (started.current || !hasEmail || setupFailed.current || !authRef.current.authInitialized) return;
      started.current = true;
      if (attributeMode.current) {
        setPhase(authRef.current.isAuthenticated ? 'emailUnverified' : 'attributeSessionExpired');
        setInfo(authRef.current.isAuthenticated ? 'Request an email verification code, then enter it below.' : 'Please log in again to verify your email.');
      } else if (authRef.current.isAuthenticated) {
        const id = begin('loadingAccount');
        if (id !== null) loadAccount(id, true);
      } else if (verified.current) {
        const id = begin('signingIn');
        if (id !== null) signIn(id);
      }
    },
  };
  useEffect(() => { actions.current.start(); }, [auth.authInitialized, auth.isAuthenticated]);

  // Old history entries are stripped of secrets without using them for login.
  useEffect(() => {
    if (location.state?.password) navigate('/verify-email', {
      replace: true, state: { email, signupId, signupSub, verified: verified.current, setupFailed: setupFailed.current },
    });
  }, [location.state, email, signupId, signupSub, navigate]);

  return {
    email, hasEmail, phase, error, info, verified: verified.current, attributeVerification: attributeMode.current,
    initializing: !auth.authInitialized,
    busy: ['verifying', 'resending', 'signingIn', 'loadingAccount'].includes(phase),
    verify, resend, continueToLogin,
    canRetrySetup: Boolean(credentials.current?.password || auth.isAuthenticated),
    retrySetup: () => {
      const id = begin('signingIn'); if (id === null) return;
      setupFailed.current = false;
      if (authRef.current.isAuthenticated) loadAccount(id); else signIn(id);
    },
    retrySignIn: () => { const id = begin('signingIn'); if (id !== null) signIn(id); },
    retryAccount: () => { const id = begin('loadingAccount'); if (id !== null) loadAccount(id); },
  };
}
